import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Printer, QrCode, Mail, Loader2, AlertTriangle, FlaskConical } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { printOrderDocument, sendReceiptEmail, waitForPrintJob } from '../lib/api';
import { getPaymentMethodLabel } from '../lib/paymentMethods';
import { toast } from 'sonner';

export default function ReceiptDialog({ open, onClose, order, printTarget = 'receipt' }) {
  const [qrSrc, setQrSrc] = useState('');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (order?.ft_qr_data) {
      QRCode.toDataURL(order.ft_qr_data)
        .then((url) => setQrSrc(url))
        .catch(() => setQrSrc(''));
    }
  }, [order?.ft_qr_data]);

  if (!order) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handlePrint = async () => {
    if (!order?.id) return;
    setIsPrinting(true);
    try {
      const result = await printOrderDocument(order.id, printTarget);
      if (result?.job_id) {
        // The printer's device prints the job; wait for its confirmation.
        const job = await waitForPrintJob(result.job_id, { timeoutMs: 15000 });
        if (job?.status === 'failed') throw new Error(job.error || 'Druck fehlgeschlagen');
        if (job?.status !== 'done') {
          toast.warning(result.warnings?.[0] || `Noch keine Rückmeldung von ${result.device_name || 'dem Druckergerät'}`);
          return;
        }
        toast.success(`Gedruckt auf ${result.printer_name || 'Drucker'}`);
        return;
      }
      toast.success(result?.message || 'Druckauftrag gesendet');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Druckauftrag fehlgeschlagen');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Bitte gültige E-Mail-Adresse eingeben');
      return;
    }
    setIsSending(true);
    try {
      await sendReceiptEmail(order.id, email.trim());
      toast.success('Beleg erfolgreich gesendet');
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="pos-workflow-dialog max-w-md gap-0 overflow-hidden rounded-none border p-0">
        <DialogHeader className="pos-dialog-header px-5 py-4">
          <DialogTitle className="flex items-center justify-between">
            <span>Beleg</span>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={isPrinting} className="pos-dialog-secondary rounded-none">
              {isPrinting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
              Drucken
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="receipt receipt-print-area m-5 bg-white p-4 font-mono text-sm shadow-sm" data-testid="receipt">
          {/* Header */}
          <div className="text-center border-b border-dashed border-slate-300 pb-3 mb-3">
            <h3 className="font-bold text-lg">FiscalPOS</h3>
            <p className="text-xs text-slate-600">Musterstraße 1, 1010 Wien</p>
            <p className="text-xs text-slate-600">UID: ATU12345678</p>
          </div>

          {/* Info */}
          <div className="space-y-1 text-xs mb-3">
            <div className="flex justify-between">
              <span>Beleg-Nr:</span>
              <span>{order.receipt_number || order.order_number}</span>
            </div>
            <div className="flex justify-between">
              <span>Datum:</span>
              <span>{formatDate(order.completed_at || order.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span>Kasse:</span>
              <span>{order.terminal_id || 'POS_001'}</span>
            </div>
            {order.table_name && (
              <div className="flex justify-between">
                <span>Tisch:</span>
                <span>{order.table_name}</span>
              </div>
            )}
            {order.cashier_name && (
              <div className="flex justify-between">
                <span>Kellner:</span>
                <span>{order.cashier_name}</span>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="border-t border-dashed border-slate-300 pt-3 mb-3">
            {order.items?.map((item, index) => (
              <div key={index} className="flex justify-between py-1">
                <div className="flex-1">
                  <span>{item.quantity}x </span>
                  <span>{item.product_name}</span>
                </div>
                <span>€{item.total?.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-dashed border-slate-300 pt-3 space-y-1">
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Rabatt</span>
                <span>-€{order.discount_amount?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Netto</span>
              <span>€{(order.total - (order.vat_total || 0))?.toFixed(2)}</span>
            </div>
            {Object.entries(order.vat_breakdown || {}).map(([rate, amount]) => (
              <div key={rate} className="flex justify-between text-xs">
                <span>MwSt {rate}%</span>
                <span>€{amount?.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold text-lg pt-2 border-t">
              <span>GESAMT</span>
              <span>€{order.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment */}
          <div className="text-center text-xs mt-3 pt-3 border-t border-dashed border-slate-300">
            <p>Bezahlt: {getPaymentMethodLabel(order.payment_method)}</p>
          </div>

          {/* QR Code */}
          <div className="text-center mt-4 pt-3 border-t border-dashed border-slate-300">
            <div className="w-20 h-20 mx-auto mb-2">
              {qrSrc ? (
                <img src={qrSrc} alt="QR code" className="w-full h-full" />
              ) : (
                <div className="w-full h-full bg-slate-100 rounded flex items-center justify-center">
                  <QrCode className="w-12 h-12 text-slate-400" />
                </div>
              )}
            </div>
            <p className="text-[8px] text-slate-400 break-all">
              {order.ft_qr_data || 'RKSV QR-Code'}
            </p>
          </div>

          {/* Fiscal signature footer */}
          <div className="text-center text-xs mt-4 text-slate-500 space-y-1">
            {order.ft_signature === 'SIGNATUREINRICHTUNG_AUSGEFALLEN' && (
              <div className="flex items-center justify-center gap-1 text-red-600 font-medium mb-1">
                <AlertTriangle className="w-3 h-3" />
                <span>Signatureinrichtung ausgefallen</span>
              </div>
            )}
            {order.ft_signature && order.ft_signature.startsWith('MOCK-') && (
              <div className="flex items-center justify-center gap-1 text-amber-600 font-medium mb-1">
                <FlaskConical className="w-3 h-3" />
                <span>Mock-Signatur (nicht rechtskonform)</span>
              </div>
            )}
            <p className="break-all text-[8px]">
              Sig: {order.ft_signature || '—'}
            </p>
            {order.ft_hash && (
              <p className="text-[8px] text-slate-400">
                Hash: {order.ft_hash}
              </p>
            )}
            <p className="mt-2">Danke für Ihren Besuch!</p>
          </div>
        </div>

        {/* Email receipt */}
        <div className="mx-5 mb-5 flex gap-2 no-print email-send-row">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="E-Mail für Beleg…"
            className="pos-dialog-field flex-1 rounded-none px-3 py-2 text-sm"
            onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendEmail}
            disabled={isSending}
            className="pos-dialog-secondary flex-shrink-0 rounded-none"
          >
            {isSending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Mail className="w-4 h-4" />
            }
            <span className="ml-1">Senden</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
